import React, { useState, useEffect } from "react";

import useTranslation from "src/languages/useTranslation";
import { LoginMessages } from "src/languages/interfaces";

import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import Yup from "src/libraries/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { IconButton, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@libraries/icons";

import RiskButton from "src/atoms/RiskButton";

import * as Styled from "./styles";

interface Props {
  onSubmit: (password: string) => void;
  titleButton: string;
  actionConfirm: boolean;
}

interface IPasswordFormData {
  password: string;
}

const PasswordForm: React.FC<Props> = ({
  onSubmit,
  titleButton,
  actionConfirm
}: Props) => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prerequisitesMet, setPrerequisitesMet] = useState(false);
  const [filledPassword, setFilledPassword] = useState(false);

  /** Define the validation types */
  const loginSchema = Yup.object().shape({
    password: Yup.string()
      .trim()
      .required(t(LoginMessages.loginPasswordRequired))
  });

  // Form control
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
    getValues
  } = useForm<IPasswordFormData>({
    resolver: yupResolver(loginSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });

  const handleSubmitForm: SubmitHandler<IPasswordFormData> = async (
    formData: IPasswordFormData
  ) => {
    try {
      setIsLoading(true);
      onSubmit(formData.password);
    } catch (error) {
      addToast({ type: "info", title: t(LoginMessages.loginFailed) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((state) => !state);

  const handleChangePassword = () => {
    setFilledPassword(
      getValues("password") !== undefined && getValues("password") !== ""
    );
  };

  useEffect(() => {
    setPrerequisitesMet(!isLoading && actionConfirm && filledPassword);
  }, [isLoading, actionConfirm, filledPassword]);

  return (
    <form
      onSubmit={handleSubmit(handleSubmitForm)}
      onChange={handleChangePassword}
    >
      <Controller // Password
        name="password"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            autoComplete="off"
            label={t(LoginMessages.loginPasswordLabel)}
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            size="small"
            error={!!formErrors.password}
            helperText={formErrors.password && formErrors.password.message}
            className="default-field"
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label="toggle password visibility"
                  edge="end"
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              )
            }}
          />
        )}
      />
      <Styled.ButtonContainer>
        <RiskButton
          active={prerequisitesMet}
          title={titleButton.toUpperCase()}
          onClick={handleSubmit(handleSubmitForm)}
        />
      </Styled.ButtonContainer>
    </form>
  );
};

export default PasswordForm;
